import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';

import {AiResponse} from './chatbot.interface';

import {ApiService} from '../shared/api';
import {
  AiGeneratedValues,
  AiGenerateValuesPayload,
  AiSourceOrTarget,
  PrefillRequestDTO,
  PrefillResponseDTO
} from '../shared/form-generator/form-generator.interfaces';
import {User, UserService} from '../shared/user';

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private currentConversationId: string;
  private userLanguage = 'en-GB'; // English default
  private userId: number;

  constructor(private apiService: ApiService,
              private userService: UserService
  ) {
    this.currentConversationId = this.generateNewConversationId();

    this.userService.getUser().pipe(
      tap((user: User) => {
        if (user.language) {
          this.userLanguage = user.language;
          this.userId = user.id;
        }
      })
    ).subscribe();
  }

  public sendProcessQuery(text: string): Observable<AiResponse> {
    const body = {
      userQuestion: text,
      conversationId: this.currentConversationId,
      language: this.userLanguage
    };

    return this.apiService.ai.post('/chat/prompt', body);
  }

  public sendFeedback(text: string): Observable<any> {
    const body = {
      conversationId: this.currentConversationId,
      feedback: text,
      userId: this.userId
    };
    return this.apiService.ai.post('/chat/feedback/submit', body);
  }

  public generateWithAI(sources: AiSourceOrTarget[], target: AiSourceOrTarget, contentGenerationIdentifier: string): Observable<AiGeneratedValues> {
    const payload: AiGenerateValuesPayload = {
      conversationId: this.generateNewConversationId(),
      language: this.userLanguage,
      contentGenerationIdentifier: contentGenerationIdentifier,
      source: sources,
      target: target
    };
    return this.apiService.ai.post('/generate/content', payload);
  }

  // TODO: Will be handled later, on backend side during the chat history implementation
  public newChat() {
    this.currentConversationId = this.generateNewConversationId();
  }

  /**
   * Generates a random UUID (version 4) string.
   *
   * This function mimics the format of standard UUIDs:
   * - 36 characters long, including hyphens.
   * - Version 4 UUID (randomly generated).
   * - Complies with UUID format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
   *   where 'y' is one of [8, 9, a, b] (variant field).
   *
   * @returns A randomly generated UUID string.
   */
  public generateNewConversationId(): string {
    // FIXME: Will be handled later, on backend side during the chat history implementation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  public generateWithAIUsingOCR(source: File, payload: PrefillRequestDTO): Observable<PrefillResponseDTO> {
    const formData: FormData = new FormData();
    formData.append('file', source);
    const targetJson = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    formData.append('target', targetJson, 'target.json');

    return this.apiService.ai.post('/document-intelligence/prefill', formData);
  }
}
